import React from "react";
import Markdown from 'markdown-to-jsx';

interface MarkdownViewerProps {
  markdown: string;
  className?: string;
  components?: Record<string, React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>>;
}

// 헤딩 컴포넌트 공통 타입 정의
interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

// 헤딩 컴포넌트들
const h1 = ({ children, ...props }: HeadingProps) => (
  <h1 style={{ 
    fontSize: '20px', 
    fontWeight: 'bold', 
    margin: '0.67em 0',
    lineHeight: '1.2'
  }} {...props}>{children}</h1>
);

const h2 = ({ children, ...props }: HeadingProps) => (
  <h2 style={{ 
    fontSize: '18px', 
    fontWeight: 'bold', 
    margin: '0.83em 0',
    lineHeight: '1.2'
  }} {...props}>{children}</h2>
);

const h3 = ({ children, ...props }: HeadingProps) => (
  <h3 style={{ 
    fontSize: '16px', 
    fontWeight: 'bold', 
    margin: '1em 0',
    lineHeight: '1.2'
  }} {...props}>{children}</h3>
);

// p 태그를 div로 대체하는 커스텀 컴포넌트
const CustomParagraph = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div 
    style={{ 
      margin: '1em 0',
      lineHeight: '1.5'
    }} 
    {...props}
  >
    {children}
  </div>
);

// a 태그를 커스텀 컴포넌트로 대체
interface CustomLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href?: string;
}

const CustomLink = ({ href, children, ...props }: CustomLinkProps) => (
  <a 
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    style={{ 
      color: 'var(--mantine-color-blue-6)',
      cursor: 'pointer',
      textDecoration: 'underline'
    }} 
    {...props}
  >
    {children}
  </a>
);

// memo 적용 및 displayName 설정
const MemoizedCustomLink = React.memo(CustomLink);
MemoizedCustomLink.displayName = 'MemoizedCustomLink';

// li 태그를 커스텀 컴포넌트로 대체
interface CustomListItemProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

const CustomListItem = ({ children, style, className }: CustomListItemProps) => (
  <div 
    style={{ 
      display: 'flex',
      alignItems: 'baseline',  // flex-start에서 baseline으로 변경 (텍스트 기준선 정렬)
      marginBottom: '1px',
      paddingLeft: '0.5em',
      ...style
    }}
    className={className}
  >
    {/* 불릿 표시 - 위치 조정 */}
    <div style={{ 
      marginRight: '8px',  // 간격 약간 증가
      lineHeight: '1' ,
      flexShrink: 0,       // 불릿이 줄어들지 않도록
      paddingTop: '0.5px',    // 미세 조정
      fontSize: '16px',
      fontWeight: 'bold',
    }}>•</div>
    {/* 실제 내용 */}
    <div style={{ 
      lineHeight: '1.5',
      flex: '1 1 auto'     // 내용이 남은 공간을 모두 차지하도록
    }}>
      {children}
    </div>
  </div>
);

// ul 태그를 커스텀 컴포넌트로 대체
interface CustomUnorderedListProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

const CustomUnorderedList = ({ children, style, className }: CustomUnorderedListProps) => (
  <div 
    style={{ 
      margin: '8px 0',     // 위아래 간격 약간 축소
      paddingLeft: '0',    // 불필요한 기본 들여쓰기 제거
      ...style
    }}
    className={className}
  >
    {children}
  </div>
);

// 테이블 컴포넌트 정의
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse', // 다시 collapse로 변경
  margin: '16px 0',
  fontSize: 'inherit',
  lineHeight: '1.4',
  border: '1px solid #ddd',
};

const cellStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #ddd', // 모든 방향의 테두리를 한 번에 설정
  textAlign: 'left' as const,
  verticalAlign: 'top',
};

const headerCellStyle: React.CSSProperties = {
  ...cellStyle,
  fontWeight: 'bold',
  borderBottom: '2px solid #ddd', // 헤더 셀 하단 테두리 강조
  backgroundColor: '#f8f9fa', // 헤더 배경색 추가
};

const CustomTable = ({ children, ...props }: React.TableHTMLAttributes<HTMLTableElement>) => (
  <table style={tableStyle} {...props}>
    {children}
  </table>
);

const CustomTableHead = (props: React.TableHTMLAttributes<HTMLTableSectionElement>) => (
  <thead
    style={{
      // 헤더 스타일 유지
    }}
    {...props}
  />
);

const CustomTableBody = (props: React.TableHTMLAttributes<HTMLTableSectionElement>) => (
  <tbody {...props} />
);

const CustomTableRow = (props: React.TableHTMLAttributes<HTMLTableRowElement>) => (
  <tr
    style={{
      // borderBottom 제거 (셀에서 처리함)
    }}
    {...props}
  />
);

const CustomTableCell = (props: React.TdHTMLAttributes<HTMLTableCellElement>) => (
  <td style={cellStyle} {...props} />
);

const CustomTableHeader = (props: React.ThHTMLAttributes<HTMLTableCellElement>) => (
  <th style={headerCellStyle} {...props} />
);

export const MarkdownViewer = ({ markdown, className, components }: MarkdownViewerProps) => {
  return (
    <div className={className}>
      <style>
        {`
        .markdown-table-container table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
          font-size: inherit;
          line-height: 1.4;
          border: 1px solid #ddd;
        }
        .markdown-table-container th {
          font-weight: bold;
          padding: 8px 12px;
          border: 1px solid #ddd;
          text-align: left;
          vertical-align: top;
          border-bottom: 1px solid #ddd;
        }
        .markdown-table-container td {
          padding: 8px 12px;
          border: 1px solid #ddd;
          text-align: left;
          vertical-align: top;
        }
        `}
      </style>
      <div className="markdown-table-container">
        <Markdown
          options={{
            overrides: {
              h1,
              h2,
              h3,
              p: CustomParagraph,
              a: MemoizedCustomLink,
              li: CustomListItem,
              ul: CustomUnorderedList,
              // 테이블 관련 컴포넌트 추가
              table: CustomTable,
              thead: CustomTableHead,
              tbody: CustomTableBody,
              tr: CustomTableRow,
              td: CustomTableCell,
              th: CustomTableHeader,
              ...components,
            },
          }}
        >
          {markdown}
        </Markdown>
      </div>
    </div>
  );
};